import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { apiFetch, ApiError, parseApiError } from '../api/client'
import type {
  DeployTicketResponse,
  PromoteDraftResponse,
  Version,
} from '../api/types'

interface VersionListResponse {
  versions: Version[]
}

interface RollbackResponse {
  live_version: string
}

export function useVersions(glowID: string) {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: ['versions', glowID],
    queryFn: async ({ signal }) => {
      const res = await apiFetch<VersionListResponse>(
        `/v1/glows/${glowID}/versions?limit=50`,
        idTokenProvider,
        { signal },
      )
      return res.versions ?? []
    },
    enabled: !!user && !!glowID,
  })
}

// invalidateVersionsAndGlow is the common cache-bust set after any
// pointer-flipping mutation in this file. Versions list, the parent
// glow doc, and the glow list all reflect live_version / draft_version
// changes.
function invalidateVersionsAndGlow(
  qc: ReturnType<typeof useQueryClient>,
  glowID: string,
) {
  qc.invalidateQueries({ queryKey: ['versions', glowID] })
  qc.invalidateQueries({ queryKey: ['glow', glowID] })
  qc.invalidateQueries({ queryKey: ['glows'] })
}

// useRollback flips the live pointer to the given version. Reused by
// the per-row "Roll back" button on older versions.
export function useRollback(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (versionID: string) =>
      apiFetch<RollbackResponse>(`/v1/glows/${glowID}/rollback`, idTokenProvider, {
        method: 'POST',
        body: { version_id: versionID },
      }),
    onSuccess: () => invalidateVersionsAndGlow(qc, glowID),
  })
}

// usePromoteDraft sets live_version to the current draft_version.
// Same version stays pinned as Draft after promotion (the spec — same
// version can be both Live and Draft simultaneously).
export function usePromoteDraft(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<PromoteDraftResponse>(
        `/v1/glows/${glowID}/promote-draft`,
        idTokenProvider,
        { method: 'POST' },
      ),
    onSuccess: () => invalidateVersionsAndGlow(qc, glowID),
  })
}

// useDeleteVersion permanently removes a version (any version except the
// live one). The server (DELETE /v1/glows/{id}/versions/{ver}) refunds the
// version's bytes to the shared per-glow data cap and refuses only the live
// version (409 version_in_use) — deleting the draft is allowed and clears
// the draft pointer server-side. Invalidates the same cache set as the
// pointer-flipping mutations so the versions list + glow doc
// (data_bytes_used) refresh.
export function useDeleteVersion(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (versionID: string) =>
      apiFetch<void>(
        `/v1/glows/${glowID}/versions/${versionID}`,
        idTokenProvider,
        { method: 'DELETE' },
      ),
    onSuccess: () => invalidateVersionsAndGlow(qc, glowID),
  })
}

// useUploadDraft is the two-step ticket+upload flow used by the
// "Upload to Versions" button. Step 1 mints a draft ticket through
// console-api (Firebase ID token); step 2 posts the multipart archive
// to glow-api's /v1/uploads/{ticket} (cross-origin, no auth header —
// the raw ticket in the URL is the auth).
//
// Error envelopes from glow-api come back in the same
// {error:{code,message}} shape, so we manually parse it and surface as
// ApiError. Network failures (CORS, offline) become ApiError(0).
export function useUploadDraft(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const ticket = await apiFetch<DeployTicketResponse>(
        `/v1/glows/${glowID}/deploy-ticket?draft=true`,
        idTokenProvider,
        { method: 'POST' },
      )
      const form = new FormData()
      form.append('archive', file, file.name)
      let res: Response
      try {
        res = await fetch(ticket.upload_url, { method: 'POST', body: form })
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        throw new ApiError(0, 'network_error', msg)
      }
      if (!res.ok) {
        throw await parseApiError(res)
      }
    },
    onSuccess: () => invalidateVersionsAndGlow(qc, glowID),
  })
}
